import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ModuleNav } from "@/components/symin/ModuleNav";
import { Button } from "@/components/ui/button";
import { AreaSelect } from "@/components/symin/AreaSelect";
import { Cube, ArrowSquareOut, Warning, ArrowsClockwise, Stack, Path, FilmSlate, Repeat, CornersOut, CornersIn } from "@phosphor-icons/react";

const PRIO = { "Priority 1": 0xe5a93c, "Priority 2": 0xfbbf24, "Priority 3": 0x94a3b8 };
const PRIO_HEX = { "Priority 1": "#C20511", "Priority 2": "#FBBF24", "Priority 3": "#94A3B8" };
const W = 14;
const HEIGHT_EXAG = 3.4;

function prospColor(v) {
  const stops = [[0.25, 0xfef3c7], [0.5, 0xf59e0b], [0.75, 0xea580c], [1.0, 0xdc2626]];
  let c = stops[stops.length - 1][1];
  for (const [t, col] of stops) { if (v <= t) { c = col; break; } }
  return new THREE.Color(c);
}
// youngest (age 0, seaward, low) = cyan  ->  oldest (age 1, inland, high) = gold
function strandColor(age) {
  return new THREE.Color().lerpColors(new THREE.Color(0x22d3ee), new THREE.Color(0xe5a93c), Math.max(0, Math.min(1, age)));
}

const LAYER_DEFS = [
  { key: "terrain", label: "Topography relief", dot: "#38BDF8" },
  { key: "prosp", label: "Prospectivity voxels", dot: "#F59E0B" },
  { key: "targets", label: "Target zones", dot: "#C20511" },
  { key: "strand", label: "Palaeo-strandlines", dot: "#22D3EE" },
  { key: "ridges", label: "Depositional ridges", dot: "#A78BFA" },
  { key: "drill", label: "Drill traces", dot: "#00B4D8" },
  { key: "grid", label: "Reference grid", dot: "#64748B" },
];

export default function Explorer3D() {
  const { id } = useParams();
  const nav = useNavigate();
  const mount = useRef(null);
  const stageRef = useRef(null);
  const rotRef = useRef(true);
  const groupsRef = useRef({});
  const coordRef = useRef(null);
  const sceneApiRef = useRef(null);
  const flyRef = useRef(false);
  const autoLoopRef = useRef(false);
  const tourLabelRef = useRef(null);
  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState(null);
  const [model, setModel] = useState(null);
  const [status, setStatus] = useState("idle"); // idle|loading|ready|pending|webgl
  const [autoRotate, setAutoRotate] = useState(true);
  const [tourActive, setTourActive] = useState(false);
  const [autoLoop, setAutoLoop] = useState(false);
  const [kiosk, setKiosk] = useState(false);
  const [layers, setLayers] = useState({
    terrain: true, prosp: true, targets: true, strand: true, ridges: true, drill: true, grid: true,
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/projects/${id}/map-data`);
        setAreas(r.data.areas || []);
        const first = (r.data.areas || []).find((a) => a.status === "complete") || (r.data.areas || [])[0];
        if (first) setArea(first.area_id);
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, [id]);

  useEffect(() => {
    if (!area) return;
    let cancelled = false;
    setStatus("loading"); setModel(null);
    api.get(`/projects/${id}/areas/${area}/model-3d`)
      .then((r) => { if (!cancelled) { setModel(r.data); setStatus("ready"); } })
      .catch((e) => { if (!cancelled) { setStatus("pending"); if (e?.response?.status !== 400) toast.error(apiError(e)); } });
    return () => { cancelled = true; };
  }, [id, area]);

  useEffect(() => { rotRef.current = autoRotate; }, [autoRotate]);
  useEffect(() => { autoLoopRef.current = autoLoop; }, [autoLoop]);

  // toggle group visibility without rebuilding the scene
  useEffect(() => {
    const g = groupsRef.current || {};
    Object.entries(layers).forEach(([k, v]) => { if (g[k]) g[k].visible = v; });
  }, [layers]);

  function flythrough() {
    const apiS = sceneApiRef.current;
    if (!apiS || !apiS.top) return;
    setAutoRotate(false);
    const { camera, controls, top } = apiS;
    flyRef.current = true;
    const startPos = camera.position.clone();
    const startTarget = controls.target.clone();
    const endTarget = top.clone();
    const endPos = new THREE.Vector3(top.x + 3.4, top.y + 4.6, top.z + 3.4);
    const dur = 5200, t0 = performance.now();
    const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    const cur = new THREE.Vector3();
    const step = (now) => {
      const t = Math.min(1, (now - t0) / dur), e = ease(t);
      camera.position.lerpVectors(startPos, endPos, e);
      camera.position.y += Math.sin(e * Math.PI) * 3.2;
      cur.lerpVectors(startTarget, endTarget, e);
      camera.lookAt(cur);
      if (t < 1 && flyRef.current) requestAnimationFrame(step);
      else { controls.target.copy(endTarget); flyRef.current = false; controls.update(); }
    };
    requestAnimationFrame(step);
  }

  function flythroughTour() {
    const apiS = sceneApiRef.current;
    if (!apiS || !(apiS.targetsMeta || []).length) return;
    setAutoRotate(false);
    setTourActive(true);
    const { camera, controls, targetsMeta } = apiS;
    flyRef.current = true;
    const metas = targetsMeta;
    const segFly = 2600, hold = 1500;
    const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    const cur = new THREE.Vector3();
    const vantage = (p) => new THREE.Vector3(p.x + 3.0, p.y + 4.2, p.z + 3.0);
    let prevPos = camera.position.clone();
    let prevLook = controls.target.clone();
    let i = 0, segStart = performance.now();
    const placeLabel = (m) => {
      const el2 = tourLabelRef.current; if (!el2) return;
      const v = m.pos.clone().project(camera);
      const w = mount.current?.clientWidth || 1, h = mount.current?.clientHeight || 1;
      el2.style.left = `${(v.x * 0.5 + 0.5) * w}px`;
      el2.style.top = `${(-v.y * 0.5 + 0.5) * h}px`;
      el2.style.display = v.z < 1 ? "block" : "none";
      el2.style.borderColor = m.color;
      const ind = (m.indicator || "").replace(/[<>]/g, "");
      el2.innerHTML = `<div style="font-weight:600;color:#fff">${(m.label || "").replace(/[<>]/g, "")}</div>`
        + `<div style="color:${m.color}">${m.priority} · ${Math.round(m.score)}% prospectivity</div>`
        + (ind ? `<div style="color:#9fb0c4;font-size:9px;font-weight:400">${ind}</div>` : "");
    };
    const loop = (now) => {
      if (!flyRef.current) { setTourActive(false); return; }
      if (i >= metas.length) {
        if (!autoLoopRef.current) { flyRef.current = false; setTourActive(false); controls.target.copy(prevLook); controls.update(); return; }
        i = 0; segStart = now;
      }
      const m = metas[i], endPos = vantage(m.pos), local = now - segStart;
      if (local <= segFly) {
        const e = ease(local / segFly);
        camera.position.lerpVectors(prevPos, endPos, e);
        camera.position.y += Math.sin(e * Math.PI) * 1.8;
        cur.lerpVectors(prevLook, m.pos, e);
        camera.lookAt(cur);
      } else if (local <= segFly + hold) {
        camera.lookAt(m.pos);
      } else {
        prevPos = camera.position.clone(); prevLook = m.pos.clone(); i += 1; segStart = now;
      }
      placeLabel(m);
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  function stopTour() { flyRef.current = false; setTourActive(false); }

  const enterKiosk = () => {
    const stg = stageRef.current;
    if (!stg) return;
    try { stg.requestFullscreen?.(); } catch (e) { /* fullscreen may be blocked */ }
    setKiosk(true); setAutoRotate(false);
    setAutoLoop(true); autoLoopRef.current = true;
    setTimeout(() => flythroughTour(), 450);
  };
  const exitKiosk = () => {
    try { if (document.fullscreenElement) document.exitFullscreen?.(); } catch (e) { /* noop */ }
    setKiosk(false); stopTour();
  };
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement) { setKiosk(false); flyRef.current = false; setTourActive(false); } };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  useEffect(() => {
    if (status !== "ready" || !model || !mount.current) return;
    const el = mount.current;
    let renderer;
    try { renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true }); }
    catch (e) { setStatus("webgl"); return; }

    const ratio = Math.max(0.25, Math.min(4, (model.size_km?.y || 1) / (model.size_km?.x || 1)));
    const D = W * ratio;
    let width = el.clientWidth, height = el.clientHeight;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x05090f, 1);
    el.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x05090f, 34, 80);
    const camera = new THREE.PerspectiveCamera(46, width / height, 0.1, 500);
    camera.position.set(W * 0.85, W * 0.95, D * 1.25);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; controls.dampingFactor = 0.08;
    controls.maxPolarAngle = Math.PI * 0.49; controls.minDistance = 5; controls.maxDistance = 70;
    controls.target.set(0, 0.4, 0);

    scene.add(new THREE.AmbientLight(0xffffff, 0.72));
    const dir = new THREE.DirectionalLight(0xffffff, 1.15); dir.position.set(12, 22, 9); scene.add(dir);
    const dir2 = new THREE.DirectionalLight(0x88aaff, 0.4); dir2.position.set(-14, 10, -12); scene.add(dir2);
    const key = new THREE.PointLight(0xe5a93c, 0.45, 60); key.position.set(0, 10, 0); scene.add(key);

    const toX = (fx) => (fx - 0.5) * W;
    const toZ = (fyLat) => -(fyLat - 0.5) * D;

    // ---------- terrain relief ----------
    const eg = model.elevation?.grid;
    let sampleElev = () => 0;
    const gTerrain = new THREE.Group(); scene.add(gTerrain);
    if (eg && eg.length && eg[0].length) {
      const nr = eg.length, nc = eg[0].length;
      sampleElev = (fx, fyLat) => {
        const rr = (1 - fyLat) * (nr - 1), cc = fx * (nc - 1);
        const r0 = Math.max(0, Math.min(nr - 1, Math.floor(rr))), c0 = Math.max(0, Math.min(nc - 1, Math.floor(cc)));
        const r1 = Math.min(nr - 1, r0 + 1), c1 = Math.min(nc - 1, c0 + 1);
        const dr = rr - r0, dc = cc - c0;
        const v = (eg[r0][c0] * (1 - dc) + eg[r0][c1] * dc) * (1 - dr) + (eg[r1][c0] * (1 - dc) + eg[r1][c1] * dc) * dr;
        return (v || 0) * HEIGHT_EXAG;
      };
      const pos = [], uv = [], idx = [];
      for (let r = 0; r < nr; r++) for (let c = 0; c < nc; c++) {
        const fx = c / (nc - 1), fyLat = 1 - r / (nr - 1);
        pos.push(toX(fx), (eg[r][c] || 0) * HEIGHT_EXAG, toZ(fyLat));
        uv.push(fx, 1 - r / (nr - 1));
      }
      for (let r = 0; r < nr - 1; r++) for (let c = 0; c < nc - 1; c++) {
        const a = r * nc + c, b = r * nc + c + 1, d = (r + 1) * nc + c, e2 = (r + 1) * nc + c + 1;
        idx.push(a, d, b, b, d, e2);
      }
      const tgeo = new THREE.BufferGeometry();
      tgeo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
      tgeo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
      tgeo.setIndex(idx); tgeo.computeVertexNormals();
      let tmat;
      if (model.prospectivity_png) {
        const tex = new THREE.TextureLoader().load(model.prospectivity_png);
        tex.colorSpace = THREE.SRGBColorSpace;
        tmat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.94, metalness: 0.02 });
      } else {
        tmat = new THREE.MeshStandardMaterial({ color: 0x0d1a2b, roughness: 1 });
      }
      const terrain = new THREE.Mesh(tgeo, tmat); terrain.name = "terrain";
      gTerrain.add(terrain);
      const edges = new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.PlaneGeometry(W, D)),
        new THREE.LineBasicMaterial({ color: 0xe5a93c, transparent: true, opacity: 0.85 }));
      edges.rotation.x = -Math.PI / 2; edges.position.y = 0.02; gTerrain.add(edges);
    } else {
      const flat = new THREE.Mesh(new THREE.PlaneGeometry(W, D),
        model.prospectivity_png
          ? new THREE.MeshStandardMaterial({ map: (() => { const t = new THREE.TextureLoader().load(model.prospectivity_png); t.colorSpace = THREE.SRGBColorSpace; return t; })(), roughness: 0.95 })
          : new THREE.MeshStandardMaterial({ color: 0x0d1a2b, roughness: 1 }));
      flat.rotation.x = -Math.PI / 2; gTerrain.add(flat);
    }

    // reference grid
    const gGrid = new THREE.Group(); scene.add(gGrid);
    const grid = new THREE.GridHelper(Math.max(W, D) * 1.5, 24, 0x1e2d47, 0x14203a);
    grid.position.y = -0.05; gGrid.add(grid);

    // ---------- prospectivity voxels rising from terrain ----------
    const gProsp = new THREE.Group(); scene.add(gProsp);
    const pg = model.prospectivity_grid;
    if (pg && pg.length) {
      const R = pg.length, C = pg[0].length;
      const cells = [];
      for (let r = 0; r < R; r++) for (let c = 0; c < C; c++) { const v = pg[r][c]; if (v > 0.45) cells.push([r, c, v]); }
      if (cells.length) {
        const box = new THREE.BoxGeometry(1, 1, 1);
        const mat = new THREE.MeshStandardMaterial({ transparent: true, opacity: 0.66, roughness: 0.8, emissiveIntensity: 0.25 });
        const inst = new THREE.InstancedMesh(box, mat, cells.length);
        const cw = (W / C) * 0.9, cd = (D / R) * 0.9;
        const dummy = new THREE.Object3D();
        cells.forEach(([r, c, v], i) => {
          const h = 0.35 + v * 4.2;
          const fx = c / (C - 1 || 1), fyLat = 1 - r / (R - 1 || 1);
          const base = sampleElev(fx, fyLat);
          dummy.position.set(toX(fx), base + h / 2 + 0.03, toZ(fyLat));
          dummy.scale.set(cw, h, cd);
          dummy.updateMatrix();
          inst.setMatrixAt(i, dummy.matrix);
          inst.setColorAt(i, prospColor(v));
        });
        inst.instanceMatrix.needsUpdate = true;
        if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
        gProsp.add(inst);
      }
    }

    // ---------- palaeo-strandlines draped on terrain ----------
    const gStrand = new THREE.Group(); scene.add(gStrand);
    (model.strandlines || []).forEach((s) => {
      const pts = (s.pts || []).map(([fx, fy]) => new THREE.Vector3(toX(fx), sampleElev(fx, fy) + 0.12, toZ(fy)));
      if (pts.length < 2) return;
      const col = strandColor(s.age ?? 0.5);
      const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts),
        new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0.92 }));
      gStrand.add(line);
    });

    // ---------- depositional ridges ----------
    const gRidge = new THREE.Group(); scene.add(gRidge);
    (model.ridges || []).forEach((rd) => {
      const pts = (rd.pts || []).map(([fx, fy]) => new THREE.Vector3(toX(fx), sampleElev(fx, fy) + 0.18, toZ(fy)));
      if (pts.length < 2) return;
      const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts),
        new THREE.LineDashedMaterial({ color: 0xa78bfa, dashSize: 0.35, gapSize: 0.2, transparent: true, opacity: 0.85 }));
      line.computeLineDistances();
      gRidge.add(line);
    });

    // ---------- target columns ----------
    const gTargets = new THREE.Group(); scene.add(gTargets);
    let topPos = null, topScore = -1;
    const targetsMeta = [];
    (model.targets || []).forEach((t, ti) => {
      const col = PRIO[t.priority] || 0x94a3b8;
      const base = sampleElev(t.x, t.y);
      const h = 1.0 + (t.prospectivity / 100) * 3.2;
      const geo = new THREE.CylinderGeometry(0.09, 0.09, h, 12);
      const mat = new THREE.MeshStandardMaterial({ color: col, emissive: col, emissiveIntensity: 0.45, roughness: 0.4 });
      const m = new THREE.Mesh(geo, mat);
      m.position.set(toX(t.x), base + h / 2, toZ(t.y));
      gTargets.add(m);
      const tip = new THREE.Vector3(toX(t.x), base + h + 0.12, toZ(t.y));
      const sph = new THREE.Mesh(new THREE.SphereGeometry(0.22, 18, 18),
        new THREE.MeshStandardMaterial({ color: col, emissive: col, emissiveIntensity: 0.7, roughness: 0.3 }));
      sph.position.copy(tip);
      gTargets.add(sph);
      targetsMeta.push({ pos: tip.clone(), label: t.label || t.global_id || t.id || t.name || `Target ${ti + 1}`,
        priority: t.priority || "Priority 3", color: PRIO_HEX[t.priority] || "#94A3B8", score: t.prospectivity || 0,
        indicator: (t.indicator || "").toString().slice(0, 48) });
      if ((t.prospectivity || 0) > topScore) { topScore = t.prospectivity || 0; topPos = tip.clone(); }
    });
    targetsMeta.sort((x, y) => y.score - x.score);

    // ---------- drill traces ----------
    const gDrill = new THREE.Group(); scene.add(gDrill);
    (model.boreholes || []).forEach((b) => {
      const base = sampleElev(b.x, b.y);
      const depth = Math.min(6, 0.6 + (b.depth_m || 40) / 40);
      const geo = new THREE.CylinderGeometry(0.045, 0.045, depth, 8);
      const mat = new THREE.MeshStandardMaterial({ color: 0x00b4d8, emissive: 0x00b4d8, emissiveIntensity: 0.35 });
      const m = new THREE.Mesh(geo, mat);
      m.position.set(toX(b.x), base - depth / 2, toZ(b.y));
      gDrill.add(m);
    });

    groupsRef.current = { terrain: gTerrain, prosp: gProsp, targets: gTargets, strand: gStrand, ridges: gRidge, drill: gDrill, grid: gGrid };
    Object.entries(layers).forEach(([k, v]) => { if (groupsRef.current[k]) groupsRef.current[k].visible = v; });
    sceneApiRef.current = { camera, controls, top: topPos, targetsMeta, home: camera.position.clone(), homeTarget: controls.target.clone() };

    // ---------- coordinate readout (raycast to terrain) ----------
    const ray = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const bb = model.bbox || [0, 0, 1, 1];
    const onMove = (ev) => {
      const rect = renderer.domElement.getBoundingClientRect();
      ndc.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      ray.setFromCamera(ndc, camera);
      const hit = ray.intersectObjects(gTerrain.children, true)[0];
      if (hit && coordRef.current) {
        const fx = hit.point.x / W + 0.5;
        const fyLat = 0.5 - hit.point.z / D;
        const lon = bb[0] + fx * (bb[2] - bb[0]);
        const lat = bb[1] + fyLat * (bb[3] - bb[1]);
        coordRef.current.textContent = `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
      }
    };
    renderer.domElement.addEventListener("pointermove", onMove);

    let raf;
    const animate = () => {
      if (!flyRef.current) { controls.autoRotate = rotRef.current; controls.autoRotateSpeed = 0.7; controls.update(); }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const onResize = () => {
      width = el.clientWidth; height = el.clientHeight;
      renderer.setSize(width, height);
      camera.aspect = width / height; camera.updateProjectionMatrix();
    };
    const ro = new ResizeObserver(onResize); ro.observe(el);

    return () => {
      cancelAnimationFrame(raf); ro.disconnect(); controls.dispose();
      renderer.domElement.removeEventListener("pointermove", onMove);
      renderer.dispose();
      if (renderer.domElement.parentNode === el) el.removeChild(renderer.domElement);
      scene.traverse((o) => { if (o.geometry) o.geometry.dispose?.(); if (o.material) { const m = o.material; (Array.isArray(m) ? m : [m]).forEach((x) => x.dispose?.()); } });
      groupsRef.current = {};
    };
  }, [status, model]);

  const nStrand = model?.strandlines?.length || 0;
  const nRidge = model?.ridges?.length || 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ModuleNav pid={id} active="drilling" />
      <div className="ml-[64px] flex flex-col h-screen">
        {!kiosk && (
        <header className="h-[52px] shrink-0 bg-card/90 backdrop-blur-md border-b border-border flex items-center gap-2 sm:gap-3 px-3 sm:px-5">
          <Cube size={18} style={{ color: "#38BDF8" }} className="shrink-0" />
          <div className="min-w-0">
            <h1 className="font-display font-semibold text-sm sm:text-lg tracking-tight truncate" data-testid="explorer-3d-title">SYMIN 3D Cockpit</h1>
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-muted-foreground -mt-0.5 hidden sm:block">Multi-Sensor Digital Twin</div>
          </div>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <AreaSelect areas={areas} value={area} onChange={setArea} accent="#38BDF8" testid="explorer-3d-area-select" />
            <Button size="sm" variant="outline" className="h-8 shrink-0 border-[#C20511]/50 text-[#C20511] hover:bg-[#C20511]/10" onClick={flythrough} data-testid="explorer-3d-flythrough" disabled={!model?.targets?.length}>
              <Path size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Flythrough</span>
            </Button>
            <Button size="sm" variant="outline" className={`h-8 shrink-0 ${tourActive ? "border-[#C20511] text-[#C20511]" : ""}`} onClick={tourActive ? stopTour : flythroughTour} data-testid="explorer-3d-tour" disabled={!model?.targets?.length}>
              <FilmSlate size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">{tourActive ? "Stop tour" : "Target tour"}</span>
            </Button>
            <Button size="sm" variant="outline" className={`h-8 shrink-0 ${autoLoop ? "border-[#C20511] text-[#C20511]" : ""}`} onClick={() => setAutoLoop((v) => !v)} data-testid="explorer-3d-autoloop" disabled={!model?.targets?.length} title="Replay the target tour continuously (kiosk / presentation mode)">
              <Repeat size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">{autoLoop ? "Loop on" : "Auto-loop"}</span>
            </Button>
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={enterKiosk} data-testid="explorer-3d-kiosk" disabled={!model?.targets?.length} title="Fullscreen kiosk mode - hides the chrome and auto-plays a looping target tour">
              <CornersOut size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Kiosk</span>
            </Button>
            <Button size="sm" variant="outline" className={`h-8 shrink-0 ${autoRotate ? "border-[#38BDF8] text-[#38BDF8]" : ""}`} onClick={() => setAutoRotate((v) => !v)} data-testid="explorer-3d-rotate">
              <ArrowsClockwise size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">{autoRotate ? "Auto-rotate on" : "Auto-rotate off"}</span>
            </Button>
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => nav(`/projects/${id}/command`)} data-testid="explorer-3d-back">
              <ArrowSquareOut size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Command Centre</span>
            </Button>
          </div>
        </header>
        )}

        <div ref={stageRef} className="relative flex-1 min-h-0 bg-[#05090F]">
          <div ref={mount} className="absolute inset-0" data-testid="explorer-3d-canvas" />
          {status === "loading" && <div className="absolute inset-0 grid place-items-center text-muted-foreground text-sm">Building digital twin…</div>}
          {status === "pending" && (
            <div className="absolute inset-0 grid place-items-center">
              <div className="flex flex-col items-center gap-2 text-muted-foreground max-w-sm text-center px-6"><Warning size={26} style={{ color: "#FBBF24" }} /><span className="text-sm">This licence area has not been processed yet. Process it in Data &amp; Setup to build its 3D digital twin.</span></div>
            </div>
          )}
          {status === "webgl" && (
            <div className="absolute inset-0 grid place-items-center">
              <div className="flex flex-col items-center gap-2 text-muted-foreground max-w-sm text-center px-6"><Warning size={26} style={{ color: "#F43F5E" }} /><span className="text-sm">3D rendering (WebGL) is unavailable in this browser/session.</span></div>
            </div>
          )}
          {status === "ready" && (
            <>
              {tourActive && (
                <div ref={tourLabelRef} data-testid="explorer-3d-tour-label"
                  className="absolute z-20 -translate-x-1/2 -translate-y-[140%] pointer-events-none px-2 py-1 rounded-md bg-[#0B1420]/90 border text-[11px] font-mono text-slate-100 whitespace-nowrap shadow-lg"
                  style={{ borderColor: "#C20511", left: 0, top: 0 }} />
              )}
              {kiosk && (
                <button onClick={exitKiosk} data-testid="explorer-3d-kiosk-exit"
                  className="absolute top-3 right-3 z-30 inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#0B1420]/85 backdrop-blur-md border border-[#C20511]/50 text-[#C20511] text-[12px] hover:bg-[#C20511]/10 transition-colors">
                  <CornersIn size={15} /> Exit kiosk
                </button>
              )}
              {!kiosk && (<>
              {/* HUD - title / stats */}
              <div className="absolute top-3 left-3 z-10 px-3 py-2.5 rounded-md bg-card/85 backdrop-blur-md border border-border max-w-[240px] animate-fade-in" data-testid="explorer-3d-legend">
                <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#C20511] mb-1.5">{model?.name} · Cockpit</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 mb-2">
                  <Hud label="Targets" value={model?.targets?.length || 0} />
                  <Hud label="Strandlines" value={nStrand} />
                  <Hud label="Ridges" value={nRidge} />
                  <Hud label="Drill traces" value={model?.boreholes?.length || 0} />
                </div>
                {["Priority 1", "Priority 2", "Priority 3"].map((p) => (
                  <div key={p} className="flex items-center gap-2 text-[11px] text-foreground mb-0.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PRIO_HEX[p] }} />{p} target</div>
                ))}
                <div className="flex items-center gap-2 text-[11px] text-foreground mt-1"><span className="h-2.5 w-6 rounded-sm" style={{ background: "linear-gradient(90deg,#22D3EE,#C20511)" }} />Strandline age (young→old)</div>
              </div>

              {/* Layer toggle panel */}
              <div className="absolute top-3 right-3 z-10 px-3 py-2.5 rounded-md bg-card/85 backdrop-blur-md border border-border w-[190px] animate-fade-in" data-testid="explorer-3d-layers">
                <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2"><Stack size={12} weight="fill" /> Layers</div>
                {LAYER_DEFS.map((l) => (
                  <button key={l.key} data-testid={`layer-toggle-${l.key}`}
                    onClick={() => setLayers((s) => ({ ...s, [l.key]: !s[l.key] }))}
                    className="w-full flex items-center gap-2 py-[3px] text-left group">
                    <span className={`h-3.5 w-3.5 rounded-[3px] border flex items-center justify-center shrink-0 transition-colors ${layers[l.key] ? "bg-[#C20511] border-[#C20511]" : "border-border bg-transparent"}`}>
                      {layers[l.key] && <span className="text-[9px] text-white font-bold leading-none">✓</span>}
                    </span>
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: l.dot }} />
                    <span className={`text-[11px] truncate ${layers[l.key] ? "text-foreground" : "text-muted-foreground"}`}>{l.label}</span>
                  </button>
                ))}
              </div>

              {/* coordinate readout */}
              <div className="absolute bottom-3 left-3 z-10 px-3 py-1.5 rounded-md bg-card/85 backdrop-blur-md border border-border text-[10px] font-mono text-muted-foreground" data-testid="explorer-3d-coords">
                <span className="text-[#38BDF8]">WGS84</span>&nbsp; <span ref={coordRef}>hover the terrain…</span>
              </div>
              <div className="absolute bottom-3 right-3 z-10 px-3 py-1.5 rounded-md bg-card/85 backdrop-blur-md border border-border text-[10px] text-muted-foreground flex items-center gap-1.5" data-testid="explorer-3d-hint">
                <Path size={12} /> Drag to orbit · scroll to zoom · right-drag to pan
              </div>
              </>)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Hud({ label, value }) {
  return (
    <div>
      <div className="text-[15px] font-display font-semibold text-foreground leading-none">{value}</div>
      <div className="text-[8px] uppercase tracking-wide text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}
